import React, { useCallback, useEffect, useState, createContext, useContext } from 'react';
import { StoreProvider, useStore } from './hooks/useStore';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { Cart } from './components/Cart';
import { HomePage } from './pages/HomePage';
import { MenuPage } from './pages/MenuPage';
import { OrderPage } from './pages/OrderPage';
import { ReservasPage } from './pages/ReservasPage';
import { VacantesPage } from './pages/VacantesPage';
import { DashboardPage } from './pages/DashboardPage';
import { ReservasDashboardPage } from './pages/ReservasDashboardPage';
import { MenuEditorPage } from './pages/MenuEditorPage';
import { LoginPage } from './pages/LoginPage';
import { CheckCircle, Lock, Wrench, Eye, EyeOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
const MAINTENANCE_PASSWORD = '1212';
function MaintenanceGate({
  children
}: {
  children: React.ReactNode;
}) {
  const [unlocked, setUnlocked] = useState(() => sessionStorage.getItem('maintenance_unlocked') === 'true');
  const [input, setInput] = useState('');
  const [error, setError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [shake, setShake] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input === MAINTENANCE_PASSWORD) {
      sessionStorage.setItem('maintenance_unlocked', 'true');
      setUnlocked(true);
      setError(false);
    } else {
      setError(true);
      setShake(true);
      setInput('');
      setTimeout(() => setShake(false), 500);
    }
  };
  if (unlocked) return <>{children}</>;
  return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-4" data-id="element-0">
      {/* Background texture */}
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPSI0IiBoZWlnaHQ9IjQiIHZpZXdCb3g9IjAgMCA0IDQiPjxwYXRoIGZpbGw9IiNmZmZmZmYiIG9wYWNpdHk9IjAuMSIgZD0iTTEgM2gxdjFIMVYzem0yIDBoMXYxSDNWM3oiLz48L3N2Zz4=')]" data-id="element-1" />

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.4
    }} className="relative w-full max-w-sm" data-id="element-2">
        {/* Card */}
        <div className="bg-[#1c1917] rounded-3xl p-8 border border-white/10 shadow-2xl" data-id="element-3">
          {/* Icon */}
          <div className="flex justify-center mb-6" data-id="element-4">
            <div className="w-16 h-16 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center" data-id="element-5">
              <Wrench className="w-7 h-7 text-amber-400" data-id="element-6" />
            </div>
          </div>

          {/* Text */}
          <div className="text-center mb-8" data-id="element-7">
            <h1 className="text-white font-bold text-xl mb-2" data-id="element-8">
              Mantenimiento General
            </h1>
            <p className="text-stone-400 text-sm leading-relaxed" data-id="element-9">
              Esta sección está temporalmente fuera de servicio. Ingresa la
              contraseña para continuar.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} data-id="element-10">
            <motion.div animate={shake ? {
            x: [-8, 8, -6, 6, -4, 4, 0]
          } : {
            x: 0
          }} transition={{
            duration: 0.4
          }} className="mb-4" data-id="element-11">
              <div className="relative" data-id="element-12">
                <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" data-id="element-13" />
                <input type={showPassword ? 'text' : 'password'} value={input} onChange={e => {
                setInput(e.target.value);
                setError(false);
              }} placeholder="Contraseña" autoFocus className={`w-full bg-black/40 border rounded-xl py-3 pl-10 pr-10 text-white text-sm outline-none transition-all placeholder:text-stone-600 ${error ? 'border-red-500/60 focus:border-red-500' : 'border-white/10 focus:border-amber-500/60'}`} data-id="element-14" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-300 transition-colors" data-id="element-15">
                  {showPassword ? <EyeOff className="w-4 h-4" data-id="element-16" /> : <Eye className="w-4 h-4" data-id="element-17" />}
                </button>
              </div>
              <AnimatePresence data-id="element-18">
                {error && <motion.p initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0
              }} className="text-red-400 text-xs mt-2 pl-1 font-medium" data-id="element-19">
                    Contraseña incorrecta. Intenta de nuevo.
                  </motion.p>}
              </AnimatePresence>
            </motion.div>

            <button type="submit" className="w-full bg-amber-500 hover:bg-amber-400 active:scale-[0.98] text-black font-bold py-3 rounded-xl transition-all text-sm" data-id="element-20">
              Acceder
            </button>
          </form>
        </div>
      </motion.div>
    </div>;
}
type Toast = {
  id: number;
  message: string;
};
type ToastContextType = {
  showToast: (message: string) => void;
};
const ToastContext = createContext<ToastContextType>({
  showToast: () => {}
});
export function useToast() {
  return useContext(ToastContext);
}
function ToastProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const showToast = useCallback((message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, {
      id,
      message
    }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 2500);
  }, []);
  return <ToastContext.Provider value={{
    showToast
  }} data-id="element-21">
      {children}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] flex flex-col gap-2 items-center" data-id="element-22">
        {toasts.map(toast => <div key={toast.id} className="toast-enter bg-[#1c1917] text-white px-5 py-3 rounded-xl shadow-2xl flex items-center gap-2.5 text-sm font-medium whitespace-nowrap" data-id="element-23">
            <CheckCircle className="w-4 h-4 text-emerald-400" data-id="element-24" />
            {toast.message}
          </div>)}
      </div>
    </ToastContext.Provider>;
}
function AppContent() {
  const getPageFromPath = () => {
    const path = window.location.pathname.replace('/', '') || 'home';
    return path;
  };
  const [currentPage, setCurrentPage] = useState(getPageFromPath);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const {
    isAdmin
  } = useStore();
  // ── Page titles ──
  useEffect(() => {
    const titles: Record<string, string> = {
      home: 'Inicio',
      menu: 'Menú',
      order: 'Pedidos',
      reservas: 'Reservas',
      vacantes: 'Vacantes',
      login: 'Acceso',
      dashboard: 'Dashboard',
      'reservas-dashboard': 'Reservas Dashboard',
      'menu-editor': 'Editor de Menú'
    };
    const section = titles[currentPage] ?? currentPage;
    document.title = `La Cochera | ${section}`;
  }, [currentPage]);
  const navigate = useCallback((page: string) => {
    const path = page === 'home' ? '/' : `/${page}`;
    window.history.pushState(null, '', path);
    setCurrentPage(page);
  }, []);
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPage(getPageFromPath());
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  useEffect(() => {
    const adminRoutes = ['dashboard', 'reservas-dashboard', 'menu-editor'];
    if (adminRoutes.includes(currentPage) && !isAdmin) {
      navigate('login');
    }
  }, [currentPage, isAdmin]);
  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <MaintenanceGate data-id="element-25">
            <HomePage onNavigate={navigate} data-id="element-26" />
          </MaintenanceGate>;
      case 'menu':
        return <MenuPage onNavigate={navigate} data-id="element-27" />;
      case 'order':
        return <MaintenanceGate data-id="element-28">
            <OrderPage onOpenCart={() => setIsCartOpen(true)} data-id="element-29" />
          </MaintenanceGate>;
      case 'reservas':
        return <MaintenanceGate data-id="element-30">
            <ReservasPage data-id="element-31" />
          </MaintenanceGate>;
      case 'vacantes':
        return <MaintenanceGate data-id="element-32">
            <VacantesPage data-id="element-33" />
          </MaintenanceGate>;
      case 'login':
        return <MaintenanceGate data-id="element-34">
            <LoginPage onNavigate={navigate} data-id="element-35" />
          </MaintenanceGate>;
      case 'dashboard':
        return isAdmin ? <MaintenanceGate data-id="element-36">
            <DashboardPage data-id="element-37" />
          </MaintenanceGate> : <MaintenanceGate data-id="element-38">
            <LoginPage onNavigate={navigate} data-id="element-39" />
          </MaintenanceGate>;
      case 'reservas-dashboard':
        return isAdmin ? <MaintenanceGate data-id="element-40">
            <ReservasDashboardPage data-id="element-41" />
          </MaintenanceGate> : <MaintenanceGate data-id="element-42">
            <LoginPage onNavigate={navigate} data-id="element-43" />
          </MaintenanceGate>;
      case 'menu-editor':
        return isAdmin ? <MaintenanceGate data-id="element-44">
            <MenuEditorPage data-id="element-45" />
          </MaintenanceGate> : <MaintenanceGate data-id="element-46">
            <LoginPage onNavigate={navigate} data-id="element-47" />
          </MaintenanceGate>;
      default:
        return <MaintenanceGate data-id="element-48">
            <HomePage onNavigate={navigate} data-id="element-49" />
          </MaintenanceGate>;
    }
  };
  return <div className="min-h-screen bg-[#fffbf5] text-[#1c1917] flex flex-col" data-id="element-50">
      {currentPage !== 'menu' && <Navbar onNavigate={navigate} currentPage={currentPage} onOpenCart={() => setIsCartOpen(true)} data-id="element-51" />}
      <main className="flex-1" data-id="element-52">{renderPage()}</main>
      {currentPage !== 'menu' && <Footer onNavigate={navigate} data-id="element-53" />}
      <Cart isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} data-id="element-54" />
    </div>;
}
export function App() {
  return <StoreProvider data-id="element-55">
      <ToastProvider data-id="element-56">
        <AppContent data-id="element-57" />
      </ToastProvider>
    </StoreProvider>;
}